import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardTitle,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
} from "reactstrap";
import {
    getPaymentByIdAPI,
    updatePaymentStatusAPI,
} from "../../../../../api/adminPaymentAPIs.ts";
import { Icon } from "../../../../../components/Component";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const PaymentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [refundDetails, setRefundDetails] = useState({
    refundId: "",
    amount: "",
    reason: "",
    refundedAt: "",
  });

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        setLoading(true);
        const response = await getPaymentByIdAPI({ id });
        const paymentData = response.data.data;
        setPayment(paymentData);
        setStatus(paymentData.status);
        setRefundDetails({
          refundId: paymentData.refundDetails?.refundId || "",
          amount: paymentData.refundDetails?.amount || "",
          reason: paymentData.refundDetails?.reason || "",
          refundedAt: paymentData.refundDetails?.refundedAt
            ? new Date(paymentData.refundDetails.refundedAt).toISOString().slice(0, 16)
            : "",
        });
      } catch (error) {
        console.error("Failed to fetch payment details:", error);
        toast.error("Failed to load payment details");
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [id]);

  const handleStatusUpdate = async () => {
    try {
      const updateBody = {
        status,
        ...(status === "Refunded" && {
          refundDetails: {
            refundId: refundDetails.refundId,
            amount: refundDetails.amount,
            reason: refundDetails.reason,
            refundedAt: refundDetails.refundedAt
              ? new Date(refundDetails.refundedAt).toISOString()
              : null,
          },
        }),
      };
      await updatePaymentStatusAPI({
        id,
        body: updateBody,
      });
      setPayment((prev) => ({
        ...prev,
        status,
        ...(status === "Refunded" && { refundDetails: updateBody.refundDetails }),
      }));
      toast.success(`Payment status updated to ${status}`);
    } catch (error) {
      console.error("Failed to update payment status:", error);
      toast.error("Failed to update payment status");
    }
  };

  const handleRefundDetailChange = (field, value) => {
    setRefundDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const statusOptions = ["Pending", "Completed", "Failed", "Refunded"];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Payment Details" />
        <Content>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  if (!payment) {
    return (
      <React.Fragment>
        <Head title="Payment Details" />
        <Content>
          <div className="text-center py-4">
            <p className="text-danger mb-3">Payment not found</p>
            <Button color="primary" onClick={() => navigate("/admin/payments")}>
              Back to Payment List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Head title={`Payment Details - ${payment._id}`} />
      <Content>
        <div className="container-fluid py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="text-primary">Payment #{payment._id}</h3>
            <Button
              color="secondary"
              onClick={() => navigate("/admin/payments")}
              className="btn-icon"
            >
              <Icon name="arrow-left" /> Back to Payments
            </Button>
          </div>

          <Row>
            {/* Payment Info */}
            <Col lg={8}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Payment Information
                  </CardTitle>
                  <Row>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Customer:</strong>
                        <p className="mb-1">{`${payment.userId.first_name} ${payment.userId.last_name}`}</p>
                        <p className="text-muted">{payment.userId.email}</p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Order Details:</strong>
                        <p className="mb-1">Order ID: {payment.orderId._id}</p>
                        <p className="mb-1">
                          Order Date: {new Date(payment.orderId.orderDate).toLocaleString()}
                        </p>
                        <p className="mb-1">Order Status: {payment.orderId.orderStatus}</p>
                        <p className="text-dark fw-bold">
                          Total: ₹{payment.orderId.totalAmount.toFixed(2)}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Payment Details:</strong>
                        <p className="mb-1">Method: {payment.paymentMethod}</p>
                        <p className="mb-1">Transaction ID: {payment.transactionId}</p>
                        <p className="text-dark fw-bold">
                          Amount: ₹{payment.amount.toFixed(2)}
                        </p>
                      </div>
                      <div className="mb-4">
                        <strong className="text-muted d-block mb-1">Timeline:</strong>
                        <p className="mb-1">
                          Created: {new Date(payment.createdAt).toLocaleString()}
                        </p>
                        <p className="mb-1">
                          Updated: {new Date(payment.updatedAt).toLocaleString()}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Refund Details (if applicable) */}
              {payment.refundDetails?.refundId && (
                <Card className="shadow-sm border-0 mb-4">
                  <CardBody>
                    <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                      Refund Details
                    </CardTitle>
                    <Row>
                      <Col md={6}>
                        <div className="mb-4">
                          <strong className="text-muted d-block mb-1">Refund ID:</strong>
                          <p>{payment.refundDetails.refundId || "N/A"}</p>
                        </div>
                        <div className="mb-4">
                          <strong className="text-muted d-block mb-1">Amount:</strong>
                          <p className="text-dark fw-bold">
                            ₹{payment.refundDetails.amount || "N/A"}
                          </p>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-4">
                          <strong className="text-muted d-block mb-1">Reason:</strong>
                          <p>{payment.refundDetails.reason || "N/A"}</p>
                        </div>
                        <div className="mb-4">
                          <strong className="text-muted d-block mb-1">Refunded At:</strong>
                          <p>
                            {payment.refundDetails.refundedAt
                              ? new Date(payment.refundDetails.refundedAt).toLocaleString()
                              : "N/A"}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}
            </Col>

            {/* Status and Actions */}
            <Col lg={4}>
              <Card className="shadow-sm border-0">
                <CardBody>
                  <CardTitle tag="h5" className="text-dark fw-bold mb-4">
                    Payment Management
                  </CardTitle>
                  <Form>
                    <FormGroup>
                      <Label for="paymentStatus" className="text-muted fw-medium">
                        Status
                      </Label>
                      <Input
                        type="select"
                        name="paymentStatus"
                        id="paymentStatus"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="mb-3"
                      >
                        {statusOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    {status === "Refunded" && (
                      <>
                        <FormGroup>
                          <Label for="refundId" className="text-muted fw-medium">
                            Refund ID
                          </Label>
                          <Input
                            type="text"
                            name="refundId"
                            id="refundId"
                            value={refundDetails.refundId}
                            onChange={(e) => handleRefundDetailChange("refundId", e.target.value)}
                            className="mb-3"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="refundAmount" className="text-muted fw-medium">
                            Refund Amount
                          </Label>
                          <Input
                            type="number"
                            name="refundAmount"
                            id="refundAmount"
                            value={refundDetails.amount}
                            onChange={(e) => handleRefundDetailChange("amount", e.target.value)}
                            className="mb-3"
                            min="0"
                            step="0.01"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="refundReason" className="text-muted fw-medium">
                            Reason
                          </Label>
                          <Input
                            type="textarea"
                            name="refundReason"
                            id="refundReason"
                            value={refundDetails.reason}
                            onChange={(e) => handleRefundDetailChange("reason", e.target.value)}
                            className="mb-3"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="refundedAt" className="text-muted fw-medium">
                            Refunded At
                          </Label>
                          <Input
                            type="datetime-local"
                            name="refundedAt"
                            id="refundedAt"
                            value={refundDetails.refundedAt}
                            onChange={(e) => handleRefundDetailChange("refundedAt", e.target.value)}
                            className="mb-3"
                          />
                        </FormGroup>
                      </>
                    )}
                    <Button
                      color="primary"
                      onClick={handleStatusUpdate}
                      disabled={
                        status === payment.status &&
                        (status !== "Refunded" ||
                          !Object.values(refundDetails).some(
                            (val) => val && val !== payment.refundDetails?.[val]
                          ))
                      }
                      className="w-100"
                    >
                      Update Payment
                    </Button>
                  </Form>
                  <div className="mt-4">
                    <h6 className="text-muted fw-medium">Current Status:</h6>
                    <Badge
                      color={
                        payment.status === "Completed"
                          ? "success"
                          : payment.status === "Failed" || payment.status === "Refunded"
                          ? "danger"
                          : "warning"
                      }
                      className="px-3 py-2"
                    >
                      {payment.status}
                    </Badge>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Content>
    </React.Fragment>
  );
};

export default PaymentDetails;